import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { Mail, AlertCircle, CheckCircle2, Loader } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';

export const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifyToken = useCallback(async () => {
    try {
      setVerifying(true);
      const res = await fetch(`${API_URL}/auth/verify-invite/${token}`);
      const data = await res.json();

      if (!data.success) throw new Error(data.message);

      if (data.invitation.status === 'accepted') {
        setSuccess('Your account has already been secured. Please proceed to login.');
      }

      setInvitation(data.invitation);
    } catch (err: any) {
      setError(err.message || 'Invalid invitation link.');
    } finally {
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) verifyToken();
  }, [token, verifyToken]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, fullName, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to create account');

      alert('Account secured! Please sign in with your new password.');
      window.location.href = window.location.origin;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader size={32} className="animate-spin text-ink-faint" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 size={40} className="text-green-600 dark:text-green-400" />
        </div>
        <h1 className="display text-2xl text-ink">Account secured</h1>
        <p className="text-ink-muted mt-2 max-w-md text-sm">{success}</p>
        <Button variant="primary" className="mt-8" onClick={() => navigate('/')}>
          Go to login
        </Button>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle size={48} className="text-ink-faint mb-4" />
        <h1 className="display text-2xl text-ink">Invitation error</h1>
        <p className="text-ink-muted mt-2 max-w-md text-sm">{error}</p>
        <Button variant="ghost" className="mt-8" onClick={() => navigate('/')}>
          Go to login
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-inverse text-ink-inverse rounded-sm p-6 mb-6 border border-line">
          <p className="label-caps text-accent">Private beta invitation</p>
          <h1 className="display text-3xl mt-2">NexSlot</h1>
        </div>

        <Card accent className="p-8 space-y-6">
          <div>
            <h2 className="display text-xl text-ink">Complete your registration</h2>
            <p className="mt-2 text-sm text-ink-muted">
              You have been invited to join NexSlot as a <span className="font-semibold text-ink">{invitation?.role}</span>.
            </p>
          </div>

          <div className="p-4 bg-surface border border-line rounded-sm flex items-center gap-3">
            <Mail size={18} className="text-accent shrink-0" />
            <span className="text-sm font-semibold text-ink break-all">{invitation?.email}</span>
          </div>

          {error && (
            <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-sm font-semibold">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSignup} className="space-y-5">
            <Input
              required
              label="Your full name"
              type="text"
              placeholder="e.g. John Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Input
              required
              label="Create password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" variant="primary" full disabled={isSubmitting}>
              {isSubmitting
                ? <Loader size={18} className="animate-spin" />
                : <>Create my account <CheckCircle2 size={16} /></>}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};
